import {
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {
  AlertController,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonDatetime,
  IonDatetimeButton,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
  LoadingController,
  NavController
} from "@ionic/angular/standalone";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {addIcons} from "ionicons";
import {close, trash} from "ionicons/icons";
import {AuthService} from "@app/services";
import {firstValueFrom} from "rxjs";
import {Poll, PollForm, PollQuestion} from "@interfaces/poll";
import {PollsRepository} from "@app/repositories/polls.repository";
import {PollsService} from "@services/api/polls.service";
import {TakePictureComponent} from "@app/components/form/take-picture/take-picture.component";
import {formatISO, roundToNearestMinutes} from "date-fns";
import {flare} from "@flareapp/flare-client";

@Component({
  selector: 'app-update-poll-sheet',
  templateUrl: './update-poll-sheet.component.html',
  styleUrls: ['./update-poll-sheet.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonList,
    IonItem,
    TakePictureComponent,
    IonInput,
    IonTextarea,
    IonFooter,
    IonLabel,
    IonDatetime,
    IonDatetimeButton,
    IonModal,
    IonCheckbox,
    IonSelect,
    IonSelectOption
  ]
})
export class UpdatePollSheetComponent implements OnInit {

  @Input() model: Poll;
  @ViewChild('photo', {static: false}) photo: HTMLImageElement | undefined;
  questions: PollQuestion[] = [];

  public isLoading: WritableSignal<boolean> = signal(false);
  public places: Signal<any> = computed(() => this.authService.currentUser()?.places);

  public form: FormGroup;
  public hasPhoto: boolean = false;
  public minDate: string = formatISO(roundToNearestMinutes(new Date()));

  protected uploadProgress: number;
  protected loading: HTMLIonLoadingElement | undefined;

  private el: ElementRef = inject(ElementRef);
  private fb: FormBuilder = inject(FormBuilder);
  private authService: AuthService = inject(AuthService);

  private alertController: AlertController = inject(AlertController);
  private loadingController: LoadingController = inject(LoadingController);
  private navController: NavController = inject(NavController);
  private pollsRepository: PollsRepository = inject(PollsRepository);
  private pollsService: PollsService = inject(PollsService);

  constructor() {
    console.log(`${this.constructor.name}.construct`, this.model);
    addIcons({close, trash});

    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(5000)]],
      photo: ['', []],
      is_multiple_choice: [false, []],
      visible_from: [null,],
      visible_until: [null,],
      place_id: [null, [Validators.required]],
    });

  }

  ngOnInit() {
    console.log(`${this.constructor.name}.ngOnInit`, this.model);

    if (this.model) {
      this.form.patchValue(this.model);

      this.questions = [];
      if (this.model.questions) {
        this.model.questions.forEach(question => {
          this.questions.push(question);
        })
      }
    }

    if (this.places().length === 1) {
      this.form.get('place_id')?.setValue(this.places()[0].id);
    }
  }

  async save() {
    this.isLoading.set(true);

    let pollForm: PollForm = this.form.value;
    pollForm.new_images = [this.form.value.photo].filter(Boolean);

    console.log(`${this.constructor.name}.save: pollForm`, pollForm);

    if (this.form.valid) {
      if (!this.model.id) {
        this.create(pollForm);
      } else {
        this.update(pollForm);
      }
    } else {
      this.isLoading.set(false);
      this.form.markAllAsTouched();
    }
  }

  protected async create(pollForm: PollForm) {
    if (this.questions.length < 2) {
      const alert = await this.alertController.create({
        message: 'Bitte füge mindestens zwei Fragen hinzu.',
        buttons: ['OK'],
      });
      await alert.present();
      this.isLoading.set(false);
      return;
    }

    pollForm.questions = [];
    this.questions.forEach(q => pollForm.questions.push(q));

    console.log(`${this.constructor.name}.create`, pollForm);
    const poll = await firstValueFrom(this.pollsService.create(pollForm)).catch((error) => {
      this.onError(error);
      return;
    });

    if (poll) {
      await this.onSuccess(poll);
    }
  }

  protected async update(pollForm: PollForm) {
    if (this.questions.length < 2) {
      const alert = await this.alertController.create({
        message: 'Bitte füge mindestens zwei Fragen hinzu.',
        buttons: ['OK'],
      });
      await alert.present();
      this.isLoading.set(false);
      return;
    }

    pollForm.id = this.model.id;
    pollForm.images = this.model.images;
    pollForm.questions = [];
    this.questions.forEach(q => pollForm.questions.push(q));

    const poll = await firstValueFrom(this.pollsService.update(pollForm)).catch((error) => {
      this.onError(error, true);
      return;
    });

    if (poll) {
      await this.onSuccess(poll, true);
    }
  }

  protected async onSuccess(poll: Poll, isUpdate: boolean = false) {
    this.pollsRepository.add(poll);

    const message = isUpdate ? 'Umfrage wurde erfolgreich aktualisiert.' : 'Umfrage wurde erfolgreich erstellt.';
    const alert = await this.alertController.create({
      message: message,
      buttons: ['OK'],
    });
    await alert.present();

    this.isLoading.set(false);
    this.close();
    await this.navController.navigateForward(`/articles?tab=polls`);
  }

  protected async onError(error: any, isUpdate: boolean = false) {
    console.error(`${this.constructor.name}.save: error`, error);
    const message = isUpdate ? 'Umfrage konnte nicht aktualisiert werden, bitte versuche es erneut.' : 'Umfrage konnte nicht erstellt werden, bitte versuche es erneut.';
    const alert = await this.alertController.create({
      message: message,
      buttons: ['OK'],
    });
    await alert.present();

    this.isLoading.set(false);
    flare.report(error);
  }

  close() {
    this.el.nativeElement.dispatchEvent(new CustomEvent('close-update-poll-sheet', {bubbles: true}));
  }

  async addQuestion() {
    const alert = await this.alertController.create({
      message: 'Frage eingeben',
      inputs: [
        {
          placeholder: 'Stelle eine Frage',
          name: 'question',
          attributes: {
            maxlength: 255,
            minLength: 0,
            required: true,
          },
        },
      ],
      buttons: [
        {
          text: 'OK',
          handler: (value: { question: string }) => {
            console.log(`${this.constructor.name}.addQuestion:`, value);
            this.questions.push({title: value.question} as PollQuestion);
          },
        }
      ]
    });
    await alert.present();
    const firstInput: HTMLInputElement | null = document.querySelector('ion-alert input');
    firstInput?.focus();
  }

  removeQuestion(question: PollQuestion) {
    this.questions = this.questions.filter(q => q !== question);
  }
}
